/**
 * Production-safe logging utility for ALTO ARMOR
 * 
 * Provides environment-aware logging that:
 * - In development: Logs full error details for debugging
 * - In production: Logs sanitized messages without sensitive details
 * 
 * Usage:
 * ```typescript
 * import { logger } from '@/lib/logger';
 * 
 * logger.error('Operation failed', error);
 * logger.warn('Potential issue', { context: 'value' });
 * logger.info('Operation completed');
 * logger.debug('Debug details', data);
 * ```
 */

const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * Sanitize error for production logging
 * Removes sensitive details like stack traces and internal paths
 */
function sanitizeError(error: unknown): string {
  if (!error) return 'Unknown error';
  
  if (error instanceof Error) {
    // In production, only show the message without stack trace
    return error.message || 'An error occurred';
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;
    // Extract safe fields only
    if (err.message && typeof err.message === 'string') {
      return err.message;
    }
    if (err.error && typeof err.error === 'string') {
      return err.error;
    }
  }
  
  return 'An error occurred';
}

/**
 * Sanitize data objects for production logging
 * Removes potentially sensitive fields
 */
function sanitizeData(data: unknown): unknown {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const sensitiveFields = [
    'password', 'token', 'secret', 'api_key', 'apikey',
    'access_token', 'refresh_token', 'authorization', 'bearer',
    'credential', 'private_key', 'ssn', 'credit_card'
  ];
  
  const sanitized: Record<string, unknown> = {};
  const obj = data as Record<string, unknown>;
  
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveFields.some(field => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Production-safe logger
 */
export const logger = {
  /**
   * Log error messages - always logs but sanitizes in production
   */
  error: (message: string, error?: unknown, context?: Record<string, unknown>): void => {
    if (isDevelopment) {
      // Full details in development
      console.error(`[ERROR] ${message}`, error, context);
    } else {
      // Sanitized output in production
      const sanitizedError = error ? sanitizeError(error) : '';
      const sanitizedContext = context ? sanitizeData(context) : undefined;
      console.error(`[ERROR] ${message}${sanitizedError ? `: ${sanitizedError}` : ''}`, 
        sanitizedContext ? sanitizedContext : '');
    }
  },

  /**
   * Log warning messages
   */
  warn: (message: string, data?: unknown): void => {
    if (isDevelopment) {
      console.warn(`[WARN] ${message}`, data);
    } else {
      const sanitizedData = data ? sanitizeData(data) : undefined;
      console.warn(`[WARN] ${message}`, sanitizedData || '');
    }
  },

  /**
   * Log info messages - only in development
   */
  info: (message: string, data?: unknown): void => {
    if (isDevelopment) {
      console.info(`[INFO] ${message}`, data || '');
    }
    // Suppress info logs in production
  },

  /**
   * Log debug messages - only in development
   */
  debug: (message: string, data?: unknown): void => {
    if (isDevelopment) {
      console.debug(`[DEBUG] ${message}`, data || '');
    }
    // Never log debug in production
  },
};

export default logger;
